/* hydrus-gallery.js — Phase 3L
 *
 * Document-level event delegation for the shared PDP hero gallery
 * rendered by snippets/hydrus-product-gallery.liquid.
 *
 * Singleton-guarded. Initializes once per page load, discovers every
 * gallery via the [data-hydrus-gallery] attribute, and wires:
 *   1. Dot + thumb click → scroll main track to index
 *   2. Track slide → IntersectionObserver updates dot/thumb active
 *      state + announces slide change via aria-live
 *   3. Track click / Enter / Space → open lightbox at the active slide
 *      (unless gallery is waitlist or admin disabled lightbox)
 *   4. Lightbox close / prev / next / arrow-key navigation
 *   5. External video (YouTube/Vimeo) → click-to-inject iframe
 *      (defers third-party scripts until interaction)
 *   6. Native video (Shopify-hosted MP4) → muted autoplay when visible,
 *      pause when scrolled out
 *   7. Variant-image swap → subscribes to the custom event
 *      `hydrus:variant-option-change` dispatched from
 *      hydrus-apparel.js on swatch/size change. When the event's
 *      option matches the gallery's data-variant-group-key, scrolls
 *      to the first slide whose data-alt contains
 *      `#<option>:<Label>`. Jump-not-filter — all slides remain in
 *      the track.
 *
 * Data source: each gallery section emits
 *   window._hydrusGallery_<sectionId>
 * carrying { sectionId, productId, variantGroupKey, lightboxEnabled,
 * mediaCount, batchState }.
 *
 * Decoupling: this file does NOT call into hydrus-apparel.js.
 * Apparel.js owns swatch active-state + size availability + sticky
 * ATC. Gallery.js owns everything inside the [data-hydrus-gallery]
 * wrapper plus the lightbox. Communication is one custom event.
 *
 * Reduced motion: CSS handles scroll-behavior gating. JS checks the
 * media query for .scrollTo() fallback so programmatic navigation
 * also respects the preference.
 */
(function () {
  if (window.HydrusGallery) return;
  window.HydrusGallery = true;

  var PREFIX = '_hydrusGallery_';
  var EVENT_NAME = 'hydrus:variant-option-change';

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function scrollBehavior() {
    return prefersReducedMotion() ? 'auto' : 'smooth';
  }

  function getGalleries() {
    return document.querySelectorAll('[data-hydrus-gallery]');
  }

  // ─── active index, dot + thumb sync, slide announce ────────────
  function setActiveIndex(gallery, index) {
    var dots = gallery.querySelectorAll('[data-hydrus-g-dots] .hydrus-g-dot');
    var thumbs = gallery.querySelectorAll('[data-hydrus-g-thumbs] .hydrus-g-thumb');
    var slides = gallery.querySelectorAll('[data-hydrus-g-slide]');

    dots.forEach(function (d, i) { d.classList.toggle('is-active', i === index); });
    thumbs.forEach(function (t, i) { t.classList.toggle('is-active', i === index); });

    var live = gallery.querySelector('[data-hydrus-g-live-region]');
    if (live && slides.length > 1) {
      live.textContent = 'Image ' + (index + 1) + ' of ' + slides.length;
    }

    // Pause native videos outside the active slide (autoplay-on-visible
    // observer handles play, but manual scroll past a playing video
    // should also pause it cleanly).
    slides.forEach(function (slide, i) {
      var vid = slide.querySelector('[data-hydrus-g-native-video]');
      if (!vid) return;
      if (i !== index) {
        try { vid.pause(); } catch (e) { /* noop */ }
      }
    });
  }

  function scrollToIndex(gallery, index) {
    var track = gallery.querySelector('[data-hydrus-g-track]');
    if (!track) return;
    var slides = track.querySelectorAll('[data-hydrus-g-slide]');
    var slide = slides[index];
    if (!slide) return;
    track.scrollTo({ left: slide.offsetLeft, behavior: scrollBehavior() });
  }

  function currentTrackIndex(track) {
    var slides = track.querySelectorAll('[data-hydrus-g-slide]');
    var scrollLeft = track.scrollLeft;
    var bestIdx = 0;
    var bestDist = Infinity;
    slides.forEach(function (s, i) {
      var dist = Math.abs(s.offsetLeft - scrollLeft);
      if (dist < bestDist) { bestDist = dist; bestIdx = i; }
    });
    return bestIdx;
  }

  // ─── IntersectionObserver: track scroll → active index ────────
  function wireTrackObserver(gallery) {
    var track = gallery.querySelector('[data-hydrus-g-track]');
    if (!track) return;
    var slides = track.querySelectorAll('[data-hydrus-g-slide]');
    if (slides.length === 0) return;

    var obs = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var idx = Array.prototype.indexOf.call(slides, entry.target);
        if (idx >= 0) setActiveIndex(gallery, idx);
      });
    }, { root: track, threshold: 0.6 });

    slides.forEach(function (s) { obs.observe(s); });
  }

  // ─── IntersectionObserver: native-video autoplay on visible ───
  function wireVideoObserver(gallery) {
    var videos = gallery.querySelectorAll('[data-hydrus-g-native-video]');
    if (videos.length === 0) return;

    var obs = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var video = entry.target;
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          var p = video.play();
          if (p && typeof p.catch === 'function') p.catch(function () { /* autoplay blocked: silent */ });
        } else {
          try { video.pause(); } catch (e) { /* noop */ }
        }
      });
    }, { threshold: [0.5] });

    videos.forEach(function (v) { obs.observe(v); });
  }

  // ─── External video embed (YouTube / Vimeo) ───────────────────
  function embedExternalVideo(container) {
    var host = container.dataset.host;
    var id = container.dataset.externalId;
    if (!id) return;
    var src = '';
    if (host === 'youtube') {
      src = 'https://www.youtube.com/embed/' + encodeURIComponent(id) + '?autoplay=1&rel=0';
    } else if (host === 'vimeo') {
      src = 'https://player.vimeo.com/video/' + encodeURIComponent(id) + '?autoplay=1';
    }
    if (!src) return;
    var iframe = document.createElement('iframe');
    iframe.src = src;
    iframe.setAttribute('allow', 'autoplay; encrypted-media; fullscreen');
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('frameborder', '0');
    iframe.style.position = 'absolute';
    iframe.style.top = '0';
    iframe.style.left = '0';
    iframe.style.width = '100%';
    iframe.style.height = '100%';
    iframe.style.border = 'none';
    container.innerHTML = '';
    container.appendChild(iframe);
  }

  // ─── Lightbox ──────────────────────────────────────────────────
  function openLightbox(sectionId, index) {
    var dialog = document.getElementById('hydrus-gallery-lightbox-' + sectionId);
    if (!dialog) return;
    if (typeof dialog.showModal !== 'function') return; // graceful
    try { dialog.showModal(); } catch (e) { /* already open */ }

    // Delay a frame so the dialog is laid out before we measure offsetLeft
    requestAnimationFrame(function () {
      var stage = dialog.querySelector('[data-hydrus-gl-stage]');
      if (!stage) return;
      var slides = stage.querySelectorAll('[data-hydrus-gl-slide]');
      var slide = slides[index];
      if (slide) {
        stage.scrollTo({ left: slide.offsetLeft, behavior: 'auto' });
        updateLightboxCounter(dialog, index);
      }
    });
  }

  function closeLightbox(dialog) {
    if (!dialog) return;
    // Pause any playing native videos to silence audio on close
    dialog.querySelectorAll('[data-hydrus-gl-native-video]').forEach(function (v) {
      try { v.pause(); } catch (e) { /* noop */ }
    });
    if (typeof dialog.close === 'function') dialog.close();
  }

  function lightboxNavigate(dialog, direction) {
    var stage = dialog.querySelector('[data-hydrus-gl-stage]');
    if (!stage) return;
    var slides = stage.querySelectorAll('[data-hydrus-gl-slide]');
    if (slides.length === 0) return;
    var current = currentLightboxIndex(dialog);
    var nextIdx = current + direction;
    if (nextIdx < 0) nextIdx = slides.length - 1;
    if (nextIdx >= slides.length) nextIdx = 0;
    var slide = slides[nextIdx];
    if (slide) {
      stage.scrollTo({ left: slide.offsetLeft, behavior: scrollBehavior() });
      updateLightboxCounter(dialog, nextIdx);
    }
  }

  function currentLightboxIndex(dialog) {
    var stage = dialog.querySelector('[data-hydrus-gl-stage]');
    if (!stage) return 0;
    var slides = stage.querySelectorAll('[data-hydrus-gl-slide]');
    var scrollLeft = stage.scrollLeft;
    var bestIdx = 0;
    var bestDist = Infinity;
    slides.forEach(function (s, i) {
      var dist = Math.abs(s.offsetLeft - scrollLeft);
      if (dist < bestDist) { bestDist = dist; bestIdx = i; }
    });
    return bestIdx;
  }

  function updateLightboxCounter(dialog, index) {
    var counter = dialog.querySelector('[data-hydrus-gl-counter]');
    var slides = dialog.querySelectorAll('[data-hydrus-gl-slide]');
    if (counter && slides.length > 0) {
      counter.textContent = (index + 1) + ' of ' + slides.length;
    }
  }

  // ─── Variant-image swap ────────────────────────────────────────
  function handleVariantOptionChange(event) {
    var detail = event.detail || {};
    var option = (detail.option || '').toLowerCase();
    var value = detail.value;
    if (!option || !value) return;

    getGalleries().forEach(function (gallery) {
      var key = (gallery.dataset.variantGroupKey || '').toLowerCase();
      if (!key || key !== option) return;

      var track = gallery.querySelector('[data-hydrus-g-track]');
      if (!track) return;
      var slides = track.querySelectorAll('[data-hydrus-g-slide]');
      var needle = ('#' + option + ':' + value).toLowerCase();
      var matchIdx = -1;
      slides.forEach(function (slide, i) {
        if (matchIdx !== -1) return;
        var alt = (slide.dataset.alt || '').toLowerCase();
        if (alt.indexOf(needle) !== -1) matchIdx = i;
      });
      if (matchIdx !== -1) {
        var slide = slides[matchIdx];
        track.scrollTo({ left: slide.offsetLeft, behavior: scrollBehavior() });
      }
    });
  }

  // ─── Delegated click ───────────────────────────────────────────
  document.addEventListener('click', function (e) {
    // Dot
    var dot = e.target.closest('[data-hydrus-g-dots] .hydrus-g-dot');
    if (dot) {
      var g1 = dot.closest('[data-hydrus-gallery]');
      if (g1) scrollToIndex(g1, parseInt(dot.dataset.index, 10) || 0);
      return;
    }
    // Thumb
    var thumb = e.target.closest('[data-hydrus-g-thumbs] .hydrus-g-thumb');
    if (thumb) {
      var g2 = thumb.closest('[data-hydrus-gallery]');
      if (g2) scrollToIndex(g2, parseInt(thumb.dataset.index, 10) || 0);
      return;
    }
    // Lightbox close
    var close = e.target.closest('[data-hydrus-gl-close]');
    if (close) {
      closeLightbox(close.closest('dialog'));
      return;
    }
    // Lightbox prev
    var prevBtn = e.target.closest('[data-hydrus-gl-prev]');
    if (prevBtn) {
      lightboxNavigate(prevBtn.closest('dialog'), -1);
      return;
    }
    // Lightbox next
    var nextBtn = e.target.closest('[data-hydrus-gl-next]');
    if (nextBtn) {
      lightboxNavigate(nextBtn.closest('dialog'), 1);
      return;
    }
    // External video play — inside the lightbox
    var lbExtPlay = e.target.closest('.hydrus-gl-video--external .hydrus-gl-video-play');
    if (lbExtPlay) {
      var lbContainer = lbExtPlay.closest('[data-hydrus-gl-external-video]');
      if (lbContainer) embedExternalVideo(lbContainer);
      return;
    }
    // External video play — main gallery (must come BEFORE the track-click
    // branch below, otherwise clicks on the play button would open the
    // lightbox instead of playing the embed inline).
    var extPlay = e.target.closest('.hydrus-g-video--external .hydrus-g-video-play');
    if (extPlay) {
      var extContainer = extPlay.closest('[data-hydrus-g-external-video]');
      if (extContainer) embedExternalVideo(extContainer);
      return;
    }
    // Lightbox backdrop click (event.target is the dialog itself)
    if (e.target.matches && e.target.matches('dialog.hydrus-gallery-lightbox')) {
      closeLightbox(e.target);
      return;
    }
    // Main track click → open lightbox (if enabled)
    var track = e.target.closest('[data-hydrus-g-track]');
    if (track) {
      var gallery = track.closest('[data-hydrus-gallery]');
      if (!gallery || gallery.dataset.lightboxEnabled !== 'true') return;
      var slide = e.target.closest('[data-hydrus-g-slide]');
      var idx = slide ? (parseInt(slide.dataset.index, 10) || 0) : currentTrackIndex(track);
      openLightbox(gallery.dataset.sectionId, idx);
      return;
    }
  });

  // ─── Delegated keydown ─────────────────────────────────────────
  document.addEventListener('keydown', function (e) {
    // Lightbox arrow navigation
    var openDialog = document.querySelector('dialog.hydrus-gallery-lightbox[open]');
    if (openDialog) {
      if (e.key === 'ArrowLeft') { lightboxNavigate(openDialog, -1); e.preventDefault(); return; }
      if (e.key === 'ArrowRight') { lightboxNavigate(openDialog, 1); e.preventDefault(); return; }
      // ESC is native-handled by <dialog>.
      return;
    }

    // Enter/Space on focused track → open lightbox
    if (e.key !== 'Enter' && e.key !== ' ') return;
    var active = document.activeElement;
    if (!active) return;
    var track = active.matches && active.matches('[data-hydrus-g-track]')
      ? active
      : (active.closest ? active.closest('[data-hydrus-g-track]') : null);
    if (!track || track !== active) return; // only when the track itself has focus
    var gallery = track.closest('[data-hydrus-gallery]');
    if (!gallery || gallery.dataset.lightboxEnabled !== 'true') return;
    e.preventDefault();
    openLightbox(gallery.dataset.sectionId, currentTrackIndex(track));
  });

  // ─── Init ──────────────────────────────────────────────────────
  function initAll() {
    getGalleries().forEach(function (gallery) {
      wireTrackObserver(gallery);
      wireVideoObserver(gallery);
    });
  }

  document.addEventListener(EVENT_NAME, handleVariantOptionChange);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
